/* eslint-disable no-console, @typescript-eslint/ban-ts-comment, no-param-reassign */
// modules
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, isSupported } from 'firebase/messaging' // @ts-ignore
import { LocalFind, LocalInsert, LocalRemove } from '@weblite/persist'
import { envMode, envModes } from '$config/env'
import { sendFcmTokenToServer } from '$logic/push/service'
import { handleLink } from '$logic/router/url'
import { showNotificationDialog } from '$comp/modals/notification-dialog'
import { isAndroid, isInTwa } from '$func/environmentInfo'
import { registerFirebaseServiceWorker } from './register-firebase-service-worker'
import { dateWithOffset } from '$setup/time-offset'

// @ts-ignore
const firebaseConfig = JSON.parse(import.meta.env.VITE_FIREBASE_CONFIG || null)
// @ts-ignore
const VAPID_KEY = import.meta.env.VITE_VAPID_KEY

let firebaseSWRegistration: any

const setPermissionInWindow = (permission: NotificationPermission) => {
  if (!('permission' in Notification)) {
    // @ts-ignore
    Notification.permission = permission
  }
}

const promisifyNotificationRequestPermission = () => {
  try {
    return Notification.requestPermission().then(permission => {
      setPermissionInWindow(permission)

      return permission === 'granted'
        ? permission
        : Promise.reject(new Error('Unable to get permission to notify.'))
    })
  } catch (e) {
    // Note: Safari still uses the callback syntax to get the permission.
    // https://developer.mozilla.org/en-US/docs/Web/API/Notifications_API/Using_the_Notifications_API#example
    return new Promise((resolve, reject) =>
      Notification.requestPermission(permission => {
        setPermissionInWindow(permission)

        if (permission === 'granted') resolve(permission)
        else reject(new Error('Unable to get permission to notify.'))
      }),
    )
  }
}

const getNotificationPermission = () => {
  if ('permissions' in navigator && navigator.permissions.query) {
    return navigator.permissions.query({
      name: 'notifications',
    })
  }
  return Promise.reject(new Error('Can not get notification permission!'))
}

const subscribeNotifPermissionChangeEventListener = (
  permission: PermissionStatus,
) => {
  permission.onchange = () => {
    LocalRemove('lastNotifPermissionRejectionTime')
    // analite('NOTIF_STATE_CHANGE', { s: permission.state })
  }
}

export const registerNotification = async () => {
  try {
    const isMessagingSupported = await isSupported()
    if (!isMessagingSupported) {
      console.warn('firebase messaging not supported')
      return
    }

    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications.')
      return
    }
    initializeApp(firebaseConfig)
    const messaging = getMessaging()

    promisifyNotificationRequestPermission()
      .then(() => {
        return registerFirebaseServiceWorker()
        // get the token in the form of promise
      })
      .then(registration => {
        firebaseSWRegistration = registration
        return getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        })
      })
      .then((token: string) => {
        const oldToken = LocalFind('fcmRegistrationId')
        if (token !== oldToken) {
          LocalInsert('fcmRegistrationId', token)
          sendFcmTokenToServer(token)
        }
      })
      .catch(err => {
        console.error(err)
      })
  } catch (err) {
    console.error(err)
  }
}

export const checkNotificationPermissionDialog = () => {
  const lastRejectionTime = LocalFind('lastNotifPermissionRejectionTime')
  const oneWeekInMilliSeconds = 3600 * 24 * 7 * 1000

  if (!isAndroid && window.Notification?.permission === 'granted') {
    return
  }

  if (
    !lastRejectionTime ||
    dateWithOffset() - lastRejectionTime > oneWeekInMilliSeconds
  ) {
    showNotificationDialog()
  }
}

const removeFCMServiceworkerRegistration = () => {
  navigator.serviceWorker?.getRegistrations().then(function (registrations) {
    registrations.forEach(registration => {
      const scopeUrl = new URL(registration.scope)
      if (scopeUrl.pathname === '/firebase-cloud-messaging-push-scope') {
        registration.unregister()
      }
    })
  })
}

export const getUserPermissionAndRegisterNotification = () => {
  const isAndroidAndNotTWA = isAndroid && !isInTwa
  if (isAndroidAndNotTWA) {
    removeFCMServiceworkerRegistration()
  }

  if (envMode === envModes.PRODUCTION && !isAndroidAndNotTWA)
    getNotificationPermission()
      .then(permission => {
        subscribeNotifPermissionChangeEventListener(permission)
        // analite('NOTIF_STATE', { g: permission.state === 'granted' })

        if (permission.state === 'denied') return
        if (permission.state === 'granted') {
          registerNotification()
          return
        }
        if (isInTwa) {
          return
        }

        // permission state is prompt
        // NOTE: We should defer the addRouter function call until the router initialization is complete.
        setTimeout(checkNotificationPermissionDialog, 2000)
      })
      .catch(console.log)
}
export const setLastNotifPermissionRejectionTime = () =>
  LocalInsert('lastNotifPermissionRejectionTime', dateWithOffset())

export const setLastContactPermissionRejectionTime = () =>
  LocalInsert('lastContactPermissionRejectionTime', dateWithOffset())

navigator.serviceWorker?.addEventListener?.('message', event => {
  if (event.data.type === 'NOTIFICATION_CLICK')
    if (event.data.url) handleLink(event.data.url)
})

export function postMessageToFirebaseSWR(message: any) {
  if (firebaseSWRegistration?.active)
    firebaseSWRegistration.active.postMessage(message)
}
